import { useEffect, useState } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, Check, AlertCircle, X, RotateCcw, GripVertical } from 'lucide-react';
import useProducts, { invalidateProducts } from '../hooks/useProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_OPTIONS = ['Entry', 'Classic', 'Premium', 'Travel', 'Flagship'];

// Edit modal for a single set. Pre-fills with the merged data (base + override
// applied) and PUTs the changes to /api/admin/sets/{slug}. Shows a "Reset to
// original" button when an override exists.

export const EditSetDialog = ({ set, hasSetOverride, onClose, onSaved }) => {
  const { products } = useProducts();
  const [form, setForm] = useState(() => ({
    name: set.name || '',
    tier: set.tier || '',
    leather: set.leather || '',
    tagline: set.tagline || '',
    description: set.description || '',
    laptop: set.laptop || '',
    packaging: set.packaging || '',
    featured: !!set.featured,
    price: set.price ?? '',
    currency: set.currency || 'gbp',
    items: Array.isArray(set.items) ? [...set.items] : [],
    priceBreakdown: Array.isArray(set.price_breakdown)
      ? set.price_breakdown.map((li) => ({ ...li }))
      : [],
  }));
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleFeatured = () => setForm({ ...form, featured: !form.featured });

  const allCodes = Object.keys(products).sort((a, b) => a.localeCompare(b));

  const toggleItem = (code) => setForm((f) => ({
    ...f,
    items: f.items.includes(code) ? f.items.filter((c) => c !== code) : [...f.items, code],
  }));

  const handleDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (idx !== overIndex) setOverIndex(idx);
  };

  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    setOverIndex(null);
    if (dragIndex === null || dragIndex === idx) { setDragIndex(null); return; }
    setForm((f) => {
      const next = [...f.items];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return { ...f, items: next };
    });
    setDragIndex(null);
  };

  const handleDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const addLine = () => setForm((f) => ({
    ...f,
    priceBreakdown: [...f.priceBreakdown, { code: '', label: '', price: '', free: false, confirmed: true }],
  }));

  const updateLine = (i, k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = [...f.priceBreakdown];
      next[i] = { ...next[i], [k]: val };
      return { ...f, priceBreakdown: next };
    });
  };

  const removeLine = (i) => setForm((f) => ({
    ...f,
    priceBreakdown: f.priceBreakdown.filter((_, idx) => idx !== i),
  }));

  const paidTotal = form.priceBreakdown
    .filter((li) => !li.free)
    .reduce((sum, li) => sum + (Number(li.price) || 0), 0);
  const freeValue = form.priceBreakdown
    .filter((li) => li.free)
    .reduce((sum, li) => sum + (Number(li.price) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    const payload = {
      name: form.name.trim() || null,
      tier: form.tier.trim() || null,
      leather: form.leather.trim() || null,
      tagline: form.tagline.trim() || null,
      description: form.description.trim() || null,
      laptop: form.laptop.trim() || null,
      packaging: form.packaging.trim() || null,
      featured: form.featured,
      items: form.items,
      price: form.price !== '' ? Number(form.price) : null,
      currency: form.currency || null,
      price_breakdown: form.priceBreakdown.length
        ? form.priceBreakdown.map((li) => ({
            code: li.code?.trim() || null,
            label: li.label?.trim() || li.code || 'Item',
            price: Number(li.price) || 0,
            free: !!li.free,
            confirmed: li.confirmed !== false,
          }))
        : null,
    };
    try {
      await adminAxios.put(`${API}/admin/sets/${encodeURIComponent(set.slug)}`, payload);
      setResult({ ok: true, msg: 'Saved.' });
      invalidateProducts();
      onSaved?.();
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const resetToOriginal = async () => {
    if (!confirm(`Reset "${set.name}" to its original house data? Any edits will be discarded.`)) return;
    setResetBusy(true);
    try {
      await adminAxios.delete(`${API}/admin/sets/${encodeURIComponent(set.slug)}`);
      invalidateProducts();
      onSaved?.();
      onClose?.();
    } catch (err) {
      setResult({ ok: false, msg: err?.response?.data?.detail || err.message });
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-mb/95 flex items-start md:items-center justify-center overflow-y-auto py-10 px-4"
      data-testid="edit-set-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-mb-surface hairline border-gold p-6 md:p-10 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow text-gold">Editing set</div>
            <h3 className="mt-2 font-display text-3xl text-mb">
              {set.name} <span className="text-mb-muted italic">· {set.slug}</span>
            </h3>
            <div className="mt-2 eyebrow-sm text-mb-muted">
              {hasSetOverride ? 'Currently edited' : 'Original house set'}
            </div>
          </div>
          <button className="text-mb hover:text-gold p-2" onClick={onClose} data-testid="edit-set-close">
            <X size={18} strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={submit} data-testid="edit-set-form" className="mt-8">
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
            <div className="space-y-6">
              <Field label="Name" required value={form.name} onChange={update('name')} testid="edit-set-name" />
              <div>
                <label className="mb-label">Tier</label>
                <select
                  data-testid="edit-set-tier"
                  className="mb-input"
                  value={form.tier}
                  onChange={update('tier')}
                  style={{ appearance: 'none' }}
                >
                  {TIER_OPTIONS.map((t) => (
                    <option key={t} value={t} style={{ background: '#2a0d1a', color: '#F0EDE5' }}>{t}</option>
                  ))}
                </select>
              </div>
              <Field label="Leather" value={form.leather} onChange={update('leather')} testid="edit-set-leather" />
              <Field label="Tagline" value={form.tagline} onChange={update('tagline')} testid="edit-set-tagline"
                placeholder="e.g. The first impression, finished in leather." />
              <div>
                <label className="mb-label">Description</label>
                <textarea
                  rows={4}
                  className="mb-input"
                  value={form.description}
                  onChange={update('description')}
                  data-testid="edit-set-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Laptop size" value={form.laptop} onChange={update('laptop')} testid="edit-set-laptop"
                  placeholder='e.g. 15.6"' />
                <Field label="Packaging" value={form.packaging} onChange={update('packaging')} testid="edit-set-packaging"
                  placeholder="e.g. Wood gift box" />
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-3">
                <Field label="Price (blank = enquiry only)" type="number" value={form.price} onChange={update('price')} testid="edit-set-price" />
                <div>
                  <label className="mb-label">Currency</label>
                  <select
                    className="mb-input"
                    value={form.currency}
                    onChange={update('currency')}
                    data-testid="edit-set-currency"
                    style={{ appearance: 'none' }}
                  >
                    {['gbp', 'usd', 'eur'].map((c) => (
                      <option key={c} value={c} style={{ background: '#2a0d1a', color: '#F0EDE5' }}>{c.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={toggleFeatured}
                  className="w-4 h-4 accent-[var(--mb-gold)]"
                  data-testid="edit-set-featured"
                />
                <span className="eyebrow-sm text-mb">Flagship — full-width gold-bordered card</span>
              </label>

              {/* Itemised price breakdown */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="mb-label">Price breakdown (public on the set page)</label>
                  <button
                    type="button"
                    onClick={addLine}
                    className="eyebrow-sm text-gold hover:text-mb"
                    data-testid="edit-set-breakdown-add"
                  >
                    + Add line
                  </button>
                </div>
                {form.priceBreakdown.length === 0 ? (
                  <p className="mt-2 eyebrow-sm text-mb-muted">No breakdown yet — the set page will only show the total price.</p>
                ) : (
                  <div className="mt-2 hairline border-subtle divide-y divide-[var(--mb-border)]" data-testid="edit-set-breakdown-list">
                    {form.priceBreakdown.map((li, i) => (
                      <div key={i} className="p-3 space-y-2" data-testid={`edit-set-breakdown-row-${i}`}>
                        <div className="grid grid-cols-[1fr_90px_90px] gap-2">
                          <input
                            className="mb-input text-[12px]"
                            placeholder="Label, e.g. Briefcase Business Bag"
                            value={li.label}
                            onChange={updateLine(i, 'label')}
                            data-testid={`edit-set-breakdown-label-${i}`}
                          />
                          <input
                            className="mb-input text-[12px]"
                            placeholder="Code"
                            value={li.code || ''}
                            onChange={updateLine(i, 'code')}
                            data-testid={`edit-set-breakdown-code-${i}`}
                          />
                          <input
                            type="number"
                            className="mb-input text-[12px]"
                            placeholder="£"
                            value={li.price}
                            onChange={updateLine(i, 'price')}
                            data-testid={`edit-set-breakdown-price-${i}`}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!li.free}
                                onChange={updateLine(i, 'free')}
                                className="w-3.5 h-3.5 accent-[var(--mb-gold)]"
                                data-testid={`edit-set-breakdown-free-${i}`}
                              />
                              <span className="eyebrow-sm text-mb-muted">Complimentary</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={li.confirmed !== false}
                                onChange={updateLine(i, 'confirmed')}
                                className="w-3.5 h-3.5 accent-[var(--mb-gold)]"
                                data-testid={`edit-set-breakdown-confirmed-${i}`}
                              />
                              <span className={`eyebrow-sm ${li.confirmed === false ? 'text-amber-400' : 'text-mb-muted'}`}>
                                {li.confirmed === false ? 'Unconfirmed SKU — verify before shipping' : 'Confirmed'}
                              </span>
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeLine(i)}
                            className="text-mb-muted hover:text-gold p-1"
                            data-testid={`edit-set-breakdown-remove-${i}`}
                          >
                            <X size={12} strokeWidth={1.25} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {form.priceBreakdown.length > 0 && (
                  <p className="mt-2 eyebrow-sm text-mb-muted" data-testid="edit-set-breakdown-total">
                    Paid items: £{paidTotal.toFixed(2)}
                    {freeValue > 0 && ` · Complimentary value: £${freeValue.toFixed(2)}`}
                    {paidTotal !== Number(form.price || 0) && form.price !== '' && (
                      <span className="text-amber-400"> — doesn't match Price field above</span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Items panel */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <label className="mb-label">Selected pieces ({form.items.length})</label>
                </div>
                {form.items.length === 0 ? (
                  <p className="mt-2 eyebrow-sm text-mb-muted">No pieces selected — pick from the catalogue below.</p>
                ) : (
                  <ol className="mt-2 hairline border-subtle divide-y divide-[var(--mb-border)]" data-testid="edit-set-items-list">
                    {form.items.map((code, i) => (
                      <li
                        key={code}
                        draggable
                        onDragStart={handleDragStart(i)}
                        onDragOver={handleDragOver(i)}
                        onDrop={handleDrop(i)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 px-3 py-2 transition-colors ${
                          dragIndex === i ? 'opacity-40' : ''
                        } ${overIndex === i && dragIndex !== i ? 'bg-mb-rich/40 border-l-2 border-gold' : ''}`}
                        data-testid={`edit-set-item-${code.replace(/\s/g, '-')}`}
                      >
                        <GripVertical
                          size={14}
                          className="text-mb-muted cursor-grab active:cursor-grabbing shrink-0"
                          data-testid={`edit-set-drag-handle-${code.replace(/\s/g, '-')}`}
                        />
                        <span className="eyebrow-sm text-gold w-6">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1">
                          <div className="font-display text-[14px] text-mb">{products[code]?.name || code}</div>
                          <div className="eyebrow-sm text-mb-muted">{code}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleItem(code)}
                          className="text-mb-muted hover:text-gold p-1"
                          title="Remove"
                          data-testid={`edit-set-remove-${code.replace(/\s/g, '-')}`}
                        >
                          <X size={12} strokeWidth={1.25} />
                        </button>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div>
                <label className="mb-label">Catalogue</label>
                <div className="mt-2 hairline border-subtle p-3 max-h-[260px] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-1">
                    {allCodes.map((code) => {
                      const inSet = form.items.includes(code);
                      const p = products[code];
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => toggleItem(code)}
                          data-testid={`edit-set-toggle-${code.replace(/\s/g, '-')}`}
                          className={`text-left px-2 py-1.5 hairline text-[12px] transition-colors ${
                            inSet
                              ? 'border-gold text-gold bg-mb'
                              : 'border-subtle text-mb-muted hover:text-mb hover:border-mb-muted'
                          }`}
                        >
                          {inSet ? '●' : '○'}{' '}
                          <span className="text-gold">{code}</span>{' '}
                          <span className="opacity-80">{p?.name || ''}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="mt-2 eyebrow-sm text-mb-muted">Click to add. Click again to remove. Drag the ⠿ handle to reorder.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 hairline-t border-subtle pt-5 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="btn-gold disabled:opacity-50"
              disabled={busy}
              data-testid="edit-set-submit"
            >
              {busy ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : 'Save changes'}
            </button>
            {hasSetOverride && (
              <button
                type="button"
                className="btn-outline-gold disabled:opacity-50"
                onClick={resetToOriginal}
                disabled={resetBusy}
                data-testid="edit-set-reset"
              >
                {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} strokeWidth={1.25} />}
                Reset to original
              </button>
            )}
            {result && (
              <div className={`hairline px-3 py-2 ${result.ok ? 'border-gold' : 'border-subtle'}`} data-testid="edit-set-result">
                <div className={`flex items-center gap-2 eyebrow-sm ${result.ok ? 'text-gold' : 'text-mb-muted'}`}>
                  {result.ok ? <Check size={12} /> : <AlertCircle size={12} />}
                  {result.msg}
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testid, required, placeholder }) => (
  <div>
    <label className="mb-label">{label}{required && <span className="text-gold ml-1">*</span>}</label>
    <input
      type={type}
      className="mb-input"
      value={value}
      onChange={onChange}
      data-testid={testid}
      required={required}
      placeholder={placeholder}
    />
  </div>
);

export default EditSetDialog;
